import React, { useState, useEffect } from 'react';
import Tooltip from '../../ui/ToolTip';
import { usePageTooltips } from "../../../hooks/usePageTooltips";
import { HelpCircle } from 'lucide-react';

const ShiftSequenceForm = React.forwardRef(({params, schedules, existingPenalties = [], onInputChange, onAddPenalty, isDisabled, translations, isEditing, onCancelEdit}, ref) => {
  const tooltips = usePageTooltips();
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (schedules && schedules.length > 0) {
      const initialData = [];
      
      const excludedCodes = ['HF', 'XX', 'YY', 'FE', 'FR', 'Abs', 'Cmp'];
      const excludedTypes = ['Vacation', 'Holiday', 'Absence', 'Comp'];
      
      schedules.forEach((schedule, index) => {
        if (excludedCodes.includes(schedule.code)) return;
        
        if (excludedTypes.includes(schedule.schedule_type)) return;
        
        const hasOffType = schedule.shiftTypes?.some(st => 
          st.active && ['Vac', 'Ho', 'Abs', 'Comp'].includes(st.type)
        );
        if (hasOffType) return;
        
        const existingPenalty = existingPenalties.find(p => p.shift === index.toString());
        
        initialData.push({
          shift: index.toString(),
          type: 'shift',
          name: `${schedule.code} (${schedule.legend})`,
          min: existingPenalty?.hard_min || '',
          max: existingPenalty?.hard_max || '',
          preferred: existingPenalty?.soft_min || ''
        });
      });
      
      if (initialData.length > 0) {
        const existingAllExceptPenalty = existingPenalties.find(p => p.shift === '-1');
        
        initialData.push({
          shift: '-1',
          type: 'all_except_off',
          name: translations("form.shiftSequence.allExceptOff") || "TODOS HORÁRIOS MENOS FOLGA",
          min: existingAllExceptPenalty?.hard_min || '',
          max: existingAllExceptPenalty?.hard_max || '',
          preferred: existingAllExceptPenalty?.soft_min || ''
        });
      }

      setTableData(initialData);
    }
  }, [schedules, existingPenalties, translations]);

  const handleInputChange = (rowIndex, field, value) => {
    if (value !== '' && !/^\d+$/.test(value)) return;

    setTableData(prev => {
      const newData = [...prev];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [field]: value
      };
      
      const penalties = [];
      
      newData.forEach(row => {
        if (!row.min && !row.max && !row.preferred) return;
        
        const penalty = {
          shift: row.shift,
          hard_min: row.min || '',
          soft_min: row.preferred || '',
          min_penalty: '100',
          soft_max: row.preferred || '',
          hard_max: row.max || '',
          max_penalty: '100'
        };
        
        penalties.push(penalty);
      });

      onAddPenalty(penalties);
      
      return newData;
    });
  };

  const renderInputCell = (rowIndex, field, placeholder) => {
    const value = tableData[rowIndex]?.[field] || '';
    const hasValue = value !== '';
    
    return (
      <input
        type="number"
        min="1"
        max="999"
        value={value}
        onChange={(e) => handleInputChange(rowIndex, field, e.target.value)}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center transition-colors ${
          hasValue ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-300'
        }`}
        placeholder={placeholder}
        disabled={isDisabled}
      />
    );
  };

  return(
    <div ref={ref}>
      <div className="flex">
        <h2 className="text-2xl font-bold mb-6">
          {translations("form.shiftSequence.friendlyTitle") || "Sequências de Turnos"}
        </h2>
        <div className="flex items-center h-8">
          <Tooltip content={tooltips.shiftSequence}/>
        </div>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
        <h3 className="text-md font-medium text-blue-800 mb-2">
          {translations("form.shiftSequence.explanation.title") || "O que é esta página?"}
        </h3>
        <p className="text-sm text-blue-700 mb-2">
          {translations("form.shiftSequence.explanation.description") || 
           "Esta página ajuda-o a definir regras para turnos consecutivos. Por exemplo, pode querer que os enfermeiros trabalhem pelo menos 2 turnos noturnos seguidos, ou no máximo 5 turnos da manhã consecutivos."}
        </p>
        <p className="text-sm text-blue-700">
          {translations("form.shiftSequence.explanation.help") || 
           "Defina o número mínimo, máximo e preferencial de turnos consecutivos para cada tipo de turno."}
        </p>
      </div>

      {tableData.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations("form.shiftSequence.tableSequenceType") || "SEQUÊNCIAS TURNOS"}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations("form.shiftSequence.tableIdeal") || "Qual o ideal de TURNOS consecutivos"}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations("form.shiftSequence.tableMin") || "Qual é o mínimo de TURNOS consecutivas"}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations("form.shiftSequence.tableMax") || "Qual o máximo de TURNOS Consecutivas"}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tableData.map((row, index) => (
                <tr key={index} className={`hover:bg-gray-50 ${
                  row.type === 'all_except_off' ? 'bg-orange-50' : ''
                }`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`px-3 py-2 rounded-full text-sm font-medium ${
                        row.type === 'all_except_off' 
                          ? 'bg-orange-100 text-orange-800 border border-orange-200' 
                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                      }`}>
                        {row.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderInputCell(index, 'preferred', '-')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderInputCell(index, 'min', '-')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderInputCell(index, 'max', '-')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border rounded-lg p-8 text-center bg-gray-50">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {translations("form.shiftSequence.noWorkShifts.title") || "Nenhum turno de trabalho encontrado"}
          </h3>
          <p className="text-gray-500">
            {translations("form.shiftSequence.noWorkShifts.description") || 
             "Não foram encontrados turnos de trabalho válidos. As regras de sequência aplicam-se apenas a turnos de trabalho, não a folgas ou ausências."}
          </p>
        </div>
      )}
      
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-sm text-gray-600">
          <p className="font-medium text-gray-700 mb-1">
            {translations("form.shiftSequence.tableExplanation.title") || "Explicação da Tabela:"}
          </p>
          <ul className="mt-2 space-y-1 list-disc list-inside ml-2">
            <li>{translations("form.shiftSequence.tableExplanation.ideal") || "Ideal: Número preferencial de turnos consecutivos (salvo como mínimo e máximo preferido)"}</li>
            <li>{translations("form.shiftSequence.tableExplanation.min") || "Mínimo: Menor número aceitável de turnos consecutivos"}</li>
            <li>{translations("form.shiftSequence.tableExplanation.max") || "Máximo: Maior número permitido de turnos consecutivos"}</li>
          </ul>
        </div>
      </div>

      <div className='flex justify-end mt-6 gap-3'>
        {isEditing && (
          <button
            onClick={onCancelEdit}
            className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors'
          >
            {translations("form.shiftSequence.cancelEditButtonText") || "Cancelar"}
          </button>
        )}
      </div>
    </div>
  );
});

export default ShiftSequenceForm